import React, { useEffect, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import {
  AdOverviewAffiliateAd,
  CommaLists,
  GetAdOverviewAffiliatesAdsApiArg,
  useExportAffiliatesAds,
  useGetAdOverviewAffiliatesAdsQuery,
} from '../../app/api'
import { IWithExpandedAllProps, withExpanded } from '../../shared/lib/hoc/WithExpanded'
import { ColumnsType } from 'antd/lib/table'
import { IBpPaginationProps } from '../../shared/components/cards/pagination/BpPagination'
import { SorterResult } from 'antd/lib/table/interface'
import { useSorter } from '../../shared/lib/hooks/UseSorter'
import { BpTable } from '../../shared/components/cards/table/BpTable'
import { TTableResponse } from '../../shared/types/TableResponse.type'
import { LinksList } from '../ad-overview/widgets/link-list/LinksList'
import { IconButton } from '../../shared/components/buttons/icon-button/IconButton'
import { IconArrowBack } from '../../shared/components/icons/IconArrowBack'
import { BpButton } from '../../shared/components/buttons/bp-button/BpButton'
import { IconMinimize } from '../../shared/components/icons/IconMinimize'
import { IconMaximize } from '../../shared/components/icons/IconMaximize'
import { ExportDrop } from '../../shared/components/cards/export-drop/ExportDrop'
import { useAppSelector } from '../../app/store/hooks/Redux'
import { selectAdOverviewFilters } from '../ad-overview/model/adOverviewSlice'
import { IAdOverviewFilter } from '../ad-overview/model/types/AdOverviewFilter.interface'
import { useTranslation } from 'react-i18next'
import { AdCell } from '../../shared/ui/cells/AdCell'
import { RedirectLinksCell } from '../ad-overview/ui/cells/RedirectLinksCell'
import { DetailsCell } from '../ad-overview/ui/cells/DetailsCell'
import clsx from 'clsx'

import styles from './AffiliatesAdsPage.module.scss'

const Table = ({
  onToggleExpand,
  isExpandAll,
  expandedRowKeys,
  onToggleExpandAll,
}: IWithExpandedAllProps) => {
  const { t } = useTranslation()
  const { publisher_id, affiliate } = useParams()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [countRows, setCountRows] = useState<number>(0)

  const { project, country, engine, city, browser, search, dateRange, policy, adDomain } =
    useAppSelector<IAdOverviewFilter>(selectAdOverviewFilters)

  const [queryParams, setQueryParams] = useState({
    page: 1,
    'per-page': 10,
    projectId: +project || searchParams.get('project'),
    publisherId: publisher_id ? +publisher_id : undefined,
    countryId: country || Number(searchParams.get('country')),
    engineId: engine || Number(searchParams.get('engine')),
    cityId: city || Number(searchParams.get('city')),
    search: search || searchParams.get('search'),
    browserId: browser || Number(searchParams.get('browser')),
    date: searchParams.get('dateRange') || dateRange,
    policy: policy || searchParams.get('policy'),
    site: adDomain || searchParams.get('adDomain'),
  } as Partial<CommaLists<GetAdOverviewAffiliatesAdsApiArg>>)

  const { data, isLoading, isFetching } = useGetAdOverviewAffiliatesAdsQuery(
    queryParams as GetAdOverviewAffiliatesAdsApiArg,
    {
      skip: !publisher_id || queryParams == undefined,
    },
  )

  const [exportFile, { isFetching: exportAffiliatesAdsLoading }] = useExportAffiliatesAds()

  useEffect(() => {
    if (!data?.data) return
    setCountRows(data.data.total)
  }, [data])

  const columns: ColumnsType<AdOverviewAffiliateAd> = [
    {
      title: t('table.columns.ad'),
      key: 'text',
      render: (_, record) => <AdCell {...record} />,
      width: '400px',
    },
    {
      title: t('table.columns.keyword(s)'),
      dataIndex: 'keyword',
      key: 'keyword',
    },
    {
      title: t('table.columns.redirectLinks'),
      dataIndex: 'redirects',
      key: 'redirects',
      width: '150px',
      render: (_, record) => <RedirectLinksCell {...record} onToggleExpand={onToggleExpand} />,
    },
    {
      title: t('table.columns.position'),
      dataIndex: 'position_global',
      key: 'position_global',
      width: '100px',
      sorter: true,
    },
    {
      title: t('table.columns.details'),
      key: 'details',
      width: '320px',
      render: (_, { city, country, device, engine, ...record }) => (
        <DetailsCell
          {...record}
          cities={city}
          countries={country}
          devices={device}
          engines={engine}
        />
      ),
    },
    {
      title: t('table.columns.found'),
      dataIndex: 'found',
      key: 'found',
      width: '150px',
      sorter: true,
    },
  ]

  const handleTableChange = (
    { current, pageSize }: IBpPaginationProps,
    sorter?: SorterResult<AdOverviewAffiliateAd> | SorterResult<AdOverviewAffiliateAd>[],
  ) => {
    setQueryParams({ ...queryParams, page: current, 'per-page': pageSize, sort: useSorter(sorter) })
  }

  return (
    <section className={clsx(styles.wrapper)}>
      <div className={clsx(styles.header)}>
        <span className={clsx(styles.titleRow)}>
          <IconButton size='lg' onClick={() => navigate(-1)}>
            <IconArrowBack color='#00326D' />
          </IconButton>
          <h3 className={clsx(styles.title)}>
            {countRows} {t('pages.adOverview.adsFor')} “{affiliate && affiliate}”
          </h3>
        </span>
        <span className={clsx(styles.actionsRow)}>
          <BpButton
            iconLeft={isExpandAll ? <IconMinimize /> : <IconMaximize />}
            size='sm'
            theme='outline'
            type='button'
            onClick={() => {
              if (onToggleExpandAll) onToggleExpandAll(!isExpandAll)
            }}
          ></BpButton>
          <ExportDrop
            columns={[
              { keys: ['title', 'text', 'finish_domain'], label: t('table.columns.ad') },
              { keys: ['keyword'], label: t('table.columns.keyword(s)') },
              { keys: ['redirects'], label: t('table.columns.redirectLinks') },
              {
                keys: [
                  'id',
                  'country',
                  'city',
                  'engine',
                  'device',
                  'search_screen_url',
                  'link_screen_url',
                ],
                label: t('table.columns.details'),
              },
              { keys: ['found'], label: t('table.columns.found') },
            ]}
            loading={exportAffiliatesAdsLoading}
            onExport={(exportParams) => exportFile({ ...queryParams, ...exportParams })}
          />
        </span>
      </div>
      <BpTable
        expandable={{
          expandedRowRender: (record) => (
            <LinksList
              additionalRedirects={record.additionalRedirects}
              className='max-w-[980px]'
              redirects={record.redirects}
            />
          ),
          expandedRowKeys: isExpandAll
            ? data?.data?.items?.map((item) => `${item.id}`)
            : expandedRowKeys,
          showExpandColumn: false,
          expandedRowClassName: () => 'links-list',
        }}
        columns={columns}
        data={data as TTableResponse<AdOverviewAffiliateAd>}
        dataSource={data?.data?.items}
        loading={isLoading || isFetching}
        rowKey={(record) => `${record.id}`}
        onChange={(pagination, filters, sorter) => handleTableChange(pagination, sorter)}
      />
    </section>
  )
}

export const AffiliatesAdsPage = withExpanded(Table)
